// Calendar → PDF exporter.
//
// Generates a multi-page landscape PDF containing one calendar month per page,
// followed by a per-month summary section listing events, change requests, and
// holidays. Uses the shared `pdfBranding` utilities so the header/footer/logo
// match every other PDF produced by Parentum.
//
// Public entry: `generateCalendarPdf({ scope, year, monthStart, eventsByMonth,
// changeRequests, holidays, user })`
//
// scope: 'month' | 'year' | 'multi-year'
// monthStart: { year, month } — required when scope === 'month'
// year: number — required when scope === 'year'
// yearsAhead: number — used when scope === 'multi-year' (1-3)
// eventsByMonth: optional pre-grouped map { 'YYYY-MM': [event...] }; if omitted
// the caller should provide a flat `events` array which we'll group internally.

import { ensureImagesLoaded, addPdfHeader, addPdfFooterToAllPages } from './pdfBranding';

var MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
var WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function pad(n) { return n < 10 ? '0' + n : '' + n; }

function ymKey(date) {
  return date.getFullYear() + '-' + pad(date.getMonth() + 1);
}

function ymdKey(date) {
  return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate());
}

function parseISODateLocal(s) {
  // Treat 'YYYY-MM-DD' as a local date to avoid TZ shifts.
  if (!s) return null;
  var parts = String(s).slice(0, 10).split('-');
  if (parts.length !== 3) return null;
  return new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
}

function isSameYM(date, y, m) {
  return date && date.getFullYear() === y && date.getMonth() === m;
}

function eventsForMonth(events, y, m) {
  if (!events) return [];
  return events.filter(function (e) {
    var d = parseISODateLocal(e.start_date || e.date);
    return isSameYM(d, y, m);
  }).sort(function (a, b) {
    var da = (a.start_date || a.date || '') + 'T' + (a.start_time || '00:00');
    var db = (b.start_date || b.date || '') + 'T' + (b.start_time || '00:00');
    return da < db ? -1 : da > db ? 1 : 0;
  });
}

function holidaysForMonth(holidays, y, m) {
  if (!holidays) return [];
  return holidays.filter(function (h) {
    var d = parseISODateLocal(h.date);
    return isSameYM(d, y, m);
  });
}

function changeRequestsForMonth(changeRequests, y, m) {
  if (!changeRequests) return [];
  return changeRequests.filter(function (cr) {
    var d = parseISODateLocal(cr.proposed_date || cr.original_date || cr.created_at);
    return isSameYM(d, y, m);
  });
}

/* ============== Grid renderer ============== */
function drawMonthGrid(pdf, y, m, opts) {
  var pw = pdf.internal.pageSize.getWidth();
  var leftX = opts.x;
  var topY = opts.y;
  var gridW = opts.w;
  var gridH = opts.h;

  var cols = 7;
  var rows = 6;
  var cellW = gridW / cols;
  var cellH = gridH / rows;

  // Weekday header band
  var headerH = 7;
  pdf.setDrawColor(0, 0, 0);
  pdf.setLineWidth(0.3);
  pdf.setFillColor(45, 37, 96);
  pdf.rect(leftX, topY, gridW, headerH, 'F');
  pdf.setFontSize(8);
  pdf.setTextColor(255, 255, 255);
  for (var w = 0; w < 7; w++) {
    pdf.text(WEEKDAY_SHORT[w], leftX + w * cellW + cellW / 2, topY + headerH / 2 + 1.4, { align: 'center' });
  }

  var bodyTop = topY + headerH;
  var bodyH = gridH - headerH;
  cellH = bodyH / rows;

  // Cell borders
  pdf.setDrawColor(180, 180, 200);
  pdf.setLineWidth(0.2);
  for (var r = 0; r <= rows; r++) {
    pdf.line(leftX, bodyTop + r * cellH, leftX + gridW, bodyTop + r * cellH);
  }
  for (var c = 0; c <= cols; c++) {
    pdf.line(leftX + c * cellW, bodyTop, leftX + c * cellW, bodyTop + bodyH);
  }

  // First day-of-week of this month
  var first = new Date(y, m, 1);
  var startCol = first.getDay();
  var daysInMonth = new Date(y, m + 1, 0).getDate();

  pdf.setTextColor(26, 23, 56);
  for (var d = 1; d <= daysInMonth; d++) {
    var idx = startCol + d - 1;
    var rr = Math.floor(idx / 7);
    var cc = idx % 7;
    var cx = leftX + cc * cellW;
    var cy = bodyTop + rr * cellH;

    // Day number
    pdf.setFontSize(8);
    pdf.setTextColor(60, 60, 90);
    pdf.text(String(d), cx + 1.5, cy + 3);

    // Events for this day (top 3)
    var dayKey = y + '-' + pad(m + 1) + '-' + pad(d);
    var dayEvents = opts.eventsByDay[dayKey] || [];
    var maxItems = 3;
    pdf.setFontSize(5.5);
    for (var i = 0; i < Math.min(dayEvents.length, maxItems); i++) {
      var ev = dayEvents[i];
      var t = (ev.start_time ? ev.start_time + ' ' : '') + (ev.title || ev.event_type || 'Event');
      var lines = pdf.splitTextToSize(t, cellW - 3);
      pdf.setTextColor(26, 23, 56);
      pdf.text(lines[0], cx + 1.5, cy + 6 + i * 3);
    }
    if (dayEvents.length > maxItems) {
      pdf.setTextColor(150, 150, 170);
      pdf.text('+' + (dayEvents.length - maxItems) + ' more', cx + 1.5, cy + 6 + maxItems * 3);
    }

    // Holiday badge
    if (opts.holidaysByDay[dayKey]) {
      pdf.setFillColor(245, 158, 11);
      pdf.circle(cx + cellW - 2, cy + 2, 0.8, 'F');
    }
  }
}

function drawMonthSummary(pdf, y, m, opts) {
  var pw = pdf.internal.pageSize.getWidth();
  var leftX = opts.x;
  var topY = opts.y;
  var contentW = pw - leftX - 15;

  pdf.setFontSize(11);
  pdf.setTextColor(26, 23, 56);
  pdf.setFont(undefined, 'bold');
  pdf.text('Summary — ' + MONTH_NAMES[m] + ' ' + y, leftX, topY);
  pdf.setFont(undefined, 'normal');

  var lineY = topY + 6;
  var section = function (title, items, formatter) {
    pdf.setFontSize(9);
    pdf.setTextColor(45, 37, 96);
    pdf.setFont(undefined, 'bold');
    pdf.text(title + ' (' + items.length + ')', leftX, lineY);
    pdf.setFont(undefined, 'normal');
    lineY += 4;
    if (items.length === 0) {
      pdf.setFontSize(7.5);
      pdf.setTextColor(120, 120, 140);
      pdf.text('None this month.', leftX + 2, lineY);
      lineY += 5;
      return;
    }
    pdf.setFontSize(7.5);
    pdf.setTextColor(40, 40, 60);
    for (var i = 0; i < items.length; i++) {
      var txt = formatter(items[i]);
      var lines = pdf.splitTextToSize('• ' + txt, contentW - 4);
      // Page break if we'd overflow into the footer disclaimer area
      var ph = pdf.internal.pageSize.getHeight();
      if (lineY + lines.length * 3.4 > ph - 30) {
        pdf.addPage();
        lineY = addPdfHeader(pdf) + 4;
      }
      pdf.text(lines, leftX + 2, lineY);
      lineY += lines.length * 3.4 + 1.2;
    }
    lineY += 3;
  };

  // Events
  section('Events', opts.events, function (ev) {
    var d = parseISODateLocal(ev.start_date || ev.date);
    var dateStr = d ? (d.getMonth() + 1) + '/' + d.getDate() : '';
    var time = ev.start_time ? ' ' + ev.start_time : '';
    var title = ev.title || ev.event_type || 'Event';
    var type = ev.event_type ? ' [' + ev.event_type + ']' : '';
    var shared = ev.shared || ev.is_shared ? ' (shared)' : '';
    return dateStr + time + ' — ' + title + type + shared;
  });

  // Shared / Change Requests
  section('Change Requests', opts.changeRequests, function (cr) {
    var dt = parseISODateLocal(cr.proposed_date || cr.original_date);
    var dateStr = dt ? (dt.getMonth() + 1) + '/' + dt.getDate() + ' — ' : '';
    var title = cr.title || cr.event_title || cr.note || 'Change request';
    var status = cr.status ? ' [' + cr.status + ']' : '';
    var from = cr.requested_by_name ? ' (requested by ' + cr.requested_by_name + ')' : '';
    return dateStr + title + status + from;
  });

  // Holidays
  section('Federal Holidays', opts.holidays, function (h) {
    var d = parseISODateLocal(h.date);
    var dateStr = d ? (d.getMonth() + 1) + '/' + d.getDate() + ' — ' : '';
    return dateStr + (h.name || h.title || 'Holiday');
  });

  return lineY;
}

function buildEventsByDay(events) {
  var map = {};
  (events || []).forEach(function (e) {
    var d = parseISODateLocal(e.start_date || e.date);
    if (!d) return;
    var k = ymdKey(d);
    if (!map[k]) map[k] = [];
    map[k].push(e);
  });
  return map;
}

function buildHolidaysByDay(holidays) {
  var map = {};
  (holidays || []).forEach(function (h) {
    var d = parseISODateLocal(h.date);
    if (!d) return;
    map[ymdKey(d)] = h;
  });
  return map;
}

/* ============== Public entry ============== */
export async function generateCalendarPdf(opts) {
  var jspdfMod = await import('jspdf');
  var jsPDF = jspdfMod.jsPDF || jspdfMod.default;
  var pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'letter' });

  await ensureImagesLoaded();

  // Resolve scope into a list of (year, month) tuples.
  var months = [];
  if (opts.scope === 'month' && opts.monthStart) {
    months.push({ y: opts.monthStart.year, m: opts.monthStart.month });
  } else if (opts.scope === 'year' && opts.year) {
    for (var mi = 0; mi < 12; mi++) months.push({ y: opts.year, m: mi });
  } else if (opts.scope === 'multi-year' && opts.year && opts.yearsAhead) {
    var span = Math.max(1, Math.min(3, opts.yearsAhead));
    for (var k = 0; k < span; k++) {
      for (var mm = 0; mm < 12; mm++) months.push({ y: opts.year + k, m: mm });
    }
  } else {
    var n = new Date();
    months.push({ y: n.getFullYear(), m: n.getMonth() });
  }

  var eventsByDay = buildEventsByDay(opts.events);
  var holidaysByDay = buildHolidaysByDay(opts.holidays);

  // Cover / title page is skipped — we go straight to the first month so the
  // PDF stays compact, and rely on the header/footer for branding identity.
  for (var pIdx = 0; pIdx < months.length; pIdx++) {
    if (pIdx > 0) pdf.addPage();
    var contentTop = addPdfHeader(pdf);

    var pw = pdf.internal.pageSize.getWidth();
    var ph = pdf.internal.pageSize.getHeight();

    // Month title
    var ym = months[pIdx];
    pdf.setFontSize(15);
    pdf.setFont(undefined, 'bold');
    pdf.setTextColor(26, 23, 56);
    pdf.text(MONTH_NAMES[ym.m] + ' ' + ym.y, pw / 2, contentTop + 5, { align: 'center' });
    pdf.setFont(undefined, 'normal');

    // Grid — fills most of the page
    var gridY = contentTop + 8;
    var gridH = ph - gridY - 30; // leave room for footer + small summary header
    drawMonthGrid(pdf, ym.y, ym.m, {
      x: 15, y: gridY, w: pw - 30, h: gridH,
      eventsByDay: eventsByDay,
      holidaysByDay: holidaysByDay,
    });

    // Summary on a new page so we don't crowd the grid
    pdf.addPage();
    var sTop = addPdfHeader(pdf);
    drawMonthSummary(pdf, ym.y, ym.m, {
      x: 15, y: sTop + 4,
      events: eventsForMonth(opts.events, ym.y, ym.m),
      changeRequests: changeRequestsForMonth(opts.changeRequests, ym.y, ym.m),
      holidays: holidaysForMonth(opts.holidays, ym.y, ym.m),
    });
  }

  addPdfFooterToAllPages(pdf);

  var label;
  if (opts.scope === 'month' && opts.monthStart) {
    label = MONTH_NAMES[opts.monthStart.month].toLowerCase() + '-' + opts.monthStart.year;
  } else if (opts.scope === 'year') {
    label = String(opts.year);
  } else if (opts.scope === 'multi-year') {
    label = opts.year + '-to-' + (opts.year + (opts.yearsAhead - 1));
  } else {
    label = 'calendar';
  }
  pdf.save('parentum-calendar-' + label + '.pdf');
}
